import React, {forwardRef, memo, useEffect, useRef} from 'react';
import MaterialTable from "material-table";
import TableIcons from "../../utils/TableIcons";
import AddBox from "@material-ui/icons/AddBox";
import {Button} from "@material-ui/core";
import SyncIcon from "@material-ui/icons/Sync";
import setDate from "../../utils/setDate";
import RefreshIcon from "@material-ui/icons/Refresh";
import getEnvironment from "../../utils/getEnvironment";

import SignatureBlockRenderInputs from "./SignatureBlockRenderInputs";
import CheckIcon from "@material-ui/icons/Check";
import theme from "../../styles/theme";
import ClearIcon from "@material-ui/icons/Clear";
import {getIsPair, signatureBlockFilterFields} from "../../utils/signatureBlockUtils";
import useLocalStorage from "../../hooks/useLocalStorage";

const SignatureBlockTable = memo((
    {
        data,
        refetch,
        setSuccess,
        updateSignatureBlockFromTest,
        createSingleValueSignatureBlock,
        createPairValueSignatureBlock,
        updateSignatureBlock,
        deleteSignatureBlock,
        finalValue = '',
        setFinalValue,
        setModalData
    }
) => {
    const setIcon = (bool) => {
        if (bool) {
            return (
                <CheckIcon style={{color: theme.palette.success.main}}/>
            );
        }

        return <ClearIcon style={{color: theme.palette.error.main}}/>;
    };
    const env = getEnvironment();
    const tableRef = useRef();
    useEffect(() => {
        if (tableRef.current && finalValue) {
            const {dataManager, state} = tableRef.current;
            const {lastEditingRow} = state;
            if (lastEditingRow) {
                dataManager.changeRowEditing(dataManager.data[lastEditingRow.tableData.id], "update");
            }
        }
    }, [finalValue]);

    /**
     * Lookup logic
     */
    const filterFieldsLookup = {};
    data.allFilterFields.enumValues.filter(item => signatureBlockFilterFields.includes(item.name)).forEach(type => {
        filterFieldsLookup[type.name] = type.name;
    });
    const blockReasonLookup = {};
    data.allBlockReasons.enumValues.forEach(type => {
        blockReasonLookup[type.name] = type.name;
    });
    const signatureBlockSourceLookup = {};
    data.allSignatureBlockSources.enumValues.forEach(type => {
        signatureBlockSourceLookup[type.name] = type.name;
    });

    const triggerCreateForSingleValue = (newValue, newData) => {
        let blockValue = newValue
        const overrideExisting = newData.overrideExisting ? newData.overrideExisting : false

        const data = {
            ...newData,
            overrideExisting,
            blockValue
        };
        createSingleValueSignatureBlock({
            variables: {
                ...data
            }
        });
    }
    const triggerCreateForPairValue = (firstBlockValue, secondBlockValue, newData, blockReason) => {
        const overrideExisting = newData.overrideExisting ? newData.overrideExisting : false
        const data = {
            ...newData,
            firstBlockValue,
            secondBlockValue,
            overrideExisting,
            blockReason
        };
        createPairValueSignatureBlock({
            variables: {
                ...data
            }
        });
    }

    const triggerUpdate = (newValue, newData) => {

        const data = {
            ...newData,
        };

        updateSignatureBlock({
            variables: {
                ...data
            }
        });
    };

    return (
        <>
            <MaterialTable
                title=""
                tableRef={tableRef}
                icons={{
                    ...TableIcons,
                    Add: forwardRef((props, ref) => {
                        return (
                            <>
                                {env === 'prod' ? (
                                    <div>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                setSuccess(true);
                                                updateSignatureBlockFromTest();
                                            }}
                                            startIcon={<SyncIcon/>}>
                                            Sync SignatureBlocks From Test
                                        </Button>
                                    </div>
                                ) : (
                                    <AddBox {...props} ref={ref}/>
                                )}
                            </>
                        );
                    }),
                }}
                columns={[
                    {title: 'Id', field: "id", hidden: true},
                    {
                        title: '', field: "overrideExisting", hidden: false, filtering: false, editable: "onAdd",
                        render: rowData => <></>,
                        editComponent: props =>
                            <SignatureBlockRenderInputs
                                fieldType={"overrideExisting"}
                                editProps={props}
                                data={data}
                            />
                    },
                    {
                        title: "Block Value", field: "blockValue", editable: 'onAdd',
                        editComponent: props => {
                            const {rowData, value} = props;
                            const newValue = finalValue || value;
                            let {filterField} = rowData
                            const {state} = tableRef.current;
                            const {lastEditingRow, showAddRow} = state;
                            if (filterField) {
                                let isPair = getIsPair(filterField);
                                setModalData({
                                    data: rowData,
                                    value: newValue,
                                    isPair,
                                    showAddRow,
                                    triggerCreate: triggerCreateForPairValue,
                                    triggerUpdate,
                                });
                            }
                            return (
                                <SignatureBlockRenderInputs
                                    fieldType={"blockValue"}
                                    editProps={props}
                                    data={data}
                                />
                            )
                        }
                    },
                    {
                        title: "Filter Field", field: "filterField", lookup: filterFieldsLookup, editable: 'onAdd',
                        editComponent: props => (
                            <SignatureBlockRenderInputs
                                fieldType={"filterField"}
                                editProps={props}
                                data={data}
                                defaultCategory={data.filterField}
                            />
                        )
                    },
                    {
                        title: "Block Reason", field: "blockReason", lookup: blockReasonLookup, editable: 'onAdd',
                        editComponent: props => (
                            <SignatureBlockRenderInputs
                                fieldType={"blockReason"}
                                editProps={props}
                                data={data}
                                defaultCategory={data.blockReason}
                            />
                        )
                    },
                    {title: "Is Active", field: "active", lookup: {true: 'True', false: 'False'}, defaultFilter: ['true'], editable: "never", filtering: true,
                        render: rowData => setIcon(rowData.active)
                    },
                    {
                        title: "Expires", field: "expires", editable: 'always',
                        editComponent: props => (
                            <SignatureBlockRenderInputs
                                fieldType={"expires"}
                                editProps={props}
                                data={data}
                            />
                        )
                    },

                    {
                        title: "Signature Block Source", field: "signatureBlockSource", lookup: signatureBlockSourceLookup, editable: 'never',
                    },
                    {title: "Date Created", field: "dateCreated", editable: "never", filtering: false},
                    {title: "Date Modified", field: "dateModified", editable: "never", filtering: false},
                ]}
                data={data.allSignatureBlock.nodes.map(signatureBlock => {

                    return {
                        id: signatureBlock.id,
                        blockValue: signatureBlock.blockValue,
                        filterField: signatureBlock.filterField,
                        type: signatureBlock.filterListFieldType,
                        dateCreated: setDate(signatureBlock.dateCreated),
                        dateModified: setDate(signatureBlock.dateModified),
                        expires: signatureBlock.expires ? setDate(signatureBlock.expires) : 'Never',
                        blockReason: signatureBlock.blockReason,
                        active: signatureBlock.active,
                        signatureBlockSource: signatureBlock.signatureBlockSource
                    }
                })}
                editable={{
                    onRowAdd: newData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {filterField, blockValue, blockReason} = newData;
                                const newValue = finalValue || blockValue;
                                if (!filterField || !blockValue | !blockReason) {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerCreateForSingleValue(newValue, newData);
                                    resolve();
                                }

                            }, 1000);
                        }),
                    onRowUpdate: env === 'test' ? (newData, oldData) =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                const {expires} = newData;
                                if (!expires) {
                                    alert('Please fill out all required fields');
                                    reject();
                                } else {
                                    triggerUpdate(expires, newData);
                                    resolve();
                                }
                            }, 1000);
                        }) : null,
                    onRowDelete: env === 'test' ? oldData =>
                        new Promise((resolve, reject) => {
                            setTimeout(() => {
                                deleteSignatureBlock({
                                    variables: {
                                        id: oldData.id
                                    }
                                });
                                resolve();
                            }, 1000)
                        }) : null,
                    onRowAddCancelled: () => setFinalValue(''),
                    onRowUpdateCancelled: () => setFinalValue(''),
                }}
                actions={[
                    {
                        icon: () => <RefreshIcon/>,
                        tooltip: 'Refetch Data',
                        isFreeAction: true,
                        onClick: () => refetch()
                    },
                ]}
                options={{
                    addRowPosition: 'first',
                    filtering: true,
                    pageSize: 300,
                    pageSizeOptions: [300, 600, 800],
                    headerStyle: {
                        minWidth: 'auto',
                    },
                    cellStyle: {
                        minWidth: 350
                    }
                }}
            />
        </>
    );
});

export default SignatureBlockTable;
